import React, { useCallback, useEffect, useRef, useState } from "react";
import styles from "./PassengerCarTyres.module.css";
import FilterSidebar, { Option } from "../FilterSidebar/FilterSidebar";
import SortBar from "../SortBar/SortBar";
import ProductGrid from "../ProductGrid/ProductGrid";
import { setLoading } from "../../../redux/loadingSlice";
import { useDispatch } from "react-redux";
import useProductsAPI from "../../../api/useProductsAPI";
import useHeroAPI from "../../../api/useHeroAPI";
import { useLocation } from "react-router-dom";
import BreadCrumbs from "../LoginPage/BreadCrumbs/BreadCrumbs";
import AppSkeleton from "../../AppSkeleton/AppSkeleton";

const ITEMS_PER_PAGE = 9;
const PRICE_MIN = 0;
const PRICE_MAX = 50000;
const INITIAL_VISIBLE_BRANDS = 8;
const sortOptions = (a: any, b: any) => {
  const aName = String(a.name).trim();
  const bName = String(b.name).trim();

  const aNum = Number(aName);
  const bNum = Number(bName);

  const aIsNumber = !isNaN(aNum) && aName !== "";
  const bIsNumber = !isNaN(bNum) && bName !== "";

  // Numbers first
  if (aIsNumber && !bIsNumber) return -1;
  if (!aIsNumber && bIsNumber) return 1;

  // Both numbers
  if (aIsNumber && bIsNumber) {
    return aNum - bNum;
  }

  // Both text
  return aName.localeCompare(bName, undefined, {
    numeric: true,
    sensitivity: "base",
  });
};
const mapToOption = (child: any): Option => ({
  id: child.predefined_id,
  name: child.name,
});
const mapCategoryToOptions = (data: any): Option[] => {
  if (!Array.isArray(data)) return [];
  return data
    .filter((child: any) => child && child.predefined_id)
    .map((child: any) => ({
      id: child.predefined_id,
      name: child.name ?? "Unnamed",
      thumbnail: child.thumbnail ?? "",
      activeThumbnail: child.active_thumbnail ?? child.thumbnail ?? "",
    }));
};

// ─── Page Component ──────────────────────────────────────────────
const PassengerCarTyres: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortValue, setSortValue] = useState("");
  const [products, setProducts] = useState<any[]>([]);
  const [totalProducts, setTotalProducts] = useState(0);
  const [error, setError] = useState("");
  const [pageNumber, setPageNumber] = useState(1);
  const [rowsPerPageOption, setRowsPerPageOption] = useState(9);
  const [loadingMore, setLoadingMore] = useState(false);

  const loadedPages = useRef(new Set<number>());
  const observer = useRef<IntersectionObserver | null>(null);
  const sentinelNodeRef = useRef<HTMLDivElement | null>(null);
  const loadingMoreRef = useRef(loadingMore);
  const productsLengthRef = useRef(products.length);
  const totalProductsRef = useRef(totalProducts);
  const priceDebounceRef = useRef<any>(null);
  const requestIdRef = useRef(0);

  const dispatch = useDispatch();
  const location = useLocation();
  const STORAGE_KEY = "passengerCarTyresState";

  const persistedState = (() => {
    if (typeof window === "undefined") return null;
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (error) {
      console.error("Failed to parse PassengerCarTyres persisted state", error);
      return null;
    }
  })();

  const { getProductList, getProductsListCount } = useProductsAPI();
  const { predefinedFunc } = useHeroAPI();

  // Deep-link filters (used only to seed initial state)
  const seedFilters = {
    ...(persistedState || {}),
    ...(location.state || {}),
  } as any;
  const entityCode = seedFilters?.code ?? "TYRE";

  // ── Product Type (e.g. TYRE / BATTERY) ──────────────────────────
  const [productType, setProductType] = useState<any>(null);

  // ── Vehicle Type (CATEGORY tabs, e.g. 4W) ───────────────────────
  const [vehicleTypeOptions, setVehicleTypeOptions] = useState<Option[]>([]);
  const [selectedVehicleType, setSelectedVehicleType] = useState<string | null>(
    seedFilters?.category_ids?.[0] ??
      seedFilters?.tyreType?.predefined_id ??
      seedFilters?.selectedVehicleType ??
      null,
  );
  const effectiveVehicleType =
    selectedVehicleType ?? vehicleTypeOptions[0]?.id ?? null;
  // ── Vehicle Details (Make / Model / Variant) ────────────────────
  const [makes, setMakes] = useState<Option[]>([]);
  const [models, setModels] = useState<Option[]>([]);
  const [variants, setVariants] = useState<Option[]>([]);
  const [selectedMake, setSelectedMake] = useState<string | null>(
    seedFilters?.brand?.code ?? null,
  );
  const [selectedModel, setSelectedModel] = useState<string | null>(
    seedFilters?.model?.code ?? null,
  );
  const [selectedVariant, setSelectedVariant] = useState<string | null>(
    seedFilters?.variant?.code ?? null,
  );

  // ── Tyre Brand ───────────────────────────────────────────────────
  const [brandOptions, setBrandOptions] = useState<Option[]>([]);
  const [selectedBrandIds, setSelectedBrandIds] = useState<string[]>(
    seedFilters?.selectedBrand?.predefined_id
      ? [seedFilters.selectedBrand.predefined_id]
      : (seedFilters?.selectedBrandIds ?? []),
  );

  const [visibleBrandsCount, setVisibleBrandsCount] = useState(
    INITIAL_VISIBLE_BRANDS,
  );
  const [brandsLoading, setBrandsLoading] = useState(false);

  // ── Tyre Sizes (Width / Aspect / Rim) ────────────────────────────
  const [widthOptions, setWidthOptions] = useState<Option[]>([]);
  const [aspectOptions, setAspectOptions] = useState<Option[]>([]);
  const [rimOptions, setRimOptions] = useState<Option[]>([]);
  const [selectedWidth, setSelectedWidth] = useState<string | null>(
    seedFilters?.width?.code ?? seedFilters?.selectedWidth ?? null,
  );
  const [selectedAspect, setSelectedAspect] = useState<string | null>(
    seedFilters?.aspect?.code ?? seedFilters?.selectedAspect ?? null,
  );
  const [selectedRim, setSelectedRim] = useState<string | null>(
    seedFilters?.rim?.code ?? seedFilters?.selectedRim ?? null,
  );

  // ── Tyre Type / Tyre Position ────────────────────────────────────
  const [tyreTypeOptions, setTyreTypeOptions] = useState<Option[]>([]);
  const [selectedTyreTypeIds, setSelectedTyreTypeIds] = useState<string[]>(
    seedFilters?.selectedTyreTypeIds ?? [],
  );
  const [tyrePositionOptions, setTyrePositionOptions] = useState<Option[]>([]);
  const [selectedTyrePositionIds, setSelectedTyrePositionIds] = useState<
    string[]
  >(seedFilters?.selectedTyrePositionIds ?? []);

  // ── Price ─────────────────────────────────────────────────────────
  const [priceValue, setPriceValue] = useState(
    seedFilters?.priceValue ?? PRICE_MAX,
  );
  const [debouncedPrice, setDebouncedPrice] = useState(
    seedFilters?.debouncedPrice ?? PRICE_MAX,
  );

  useEffect(() => {
    try {
      if (typeof window !== "undefined") {
        localStorage.setItem(
          STORAGE_KEY,
          JSON.stringify({
            code: entityCode,
            selectedVehicleType,
            selectedMake,
            selectedModel,
            selectedVariant,
            selectedBrandIds,
            selectedWidth,
            selectedAspect,
            selectedRim,
            selectedTyreTypeIds,
            selectedTyrePositionIds,
            priceValue,
            debouncedPrice,
            sortValue,
          }),
        );
      }
    } catch (error) {
      console.error("Failed to persist PassengerCarTyres state", error);
    }
  }, [
    entityCode,
    selectedVehicleType,
    selectedMake,
    selectedModel,
    selectedVariant,
    selectedBrandIds,
    selectedWidth,
    selectedAspect,
    selectedRim,
    selectedTyreTypeIds,
    selectedTyrePositionIds,
    priceValue,
    debouncedPrice,
    sortValue,
  ]);

  useEffect(() => {
    const fetchProductType = async () => {
      const res = await predefinedFunc({
        entity_type: "PRODUCT-TYPE",
        code: entityCode,
      });
      // console.log("PRODUCT-TYPE API raw response on refresh:", res);
      if (res.status === 200) {
        // console.log("productType fetched:", res.data?.data?.[0]);
        setProductType(res.data?.data?.[0] ?? null);
      }
    };
    fetchProductType();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityCode]);
  useEffect(() => {
    loadingMoreRef.current = loadingMore;
  }, [loadingMore]);

  useEffect(() => {
    productsLengthRef.current = products.length;
  }, [products.length]);

  useEffect(() => {
    totalProductsRef.current = totalProducts;
  }, [totalProducts]);

  const categoryParentID = productType?.predefined_id ?? null;
  const EXCLUDED_CATEGORY_KEYWORDS = ["puncture"];

  // ── CATEGORY (vehicle types) — scoped to categoryParentID ───────
  useEffect(() => {
    if (!categoryParentID) return;
    let cancelled = false;

    const fetchCategories = async () => {
      try {
        const res = await predefinedFunc({
          entity_type: "CATEGORY",
          categoryParentID,
        });
        if (cancelled) return;
        if (res?.status === 200) {
          const rawOptions = mapCategoryToOptions(res.data?.data);
          const opts = rawOptions.filter(
            (opt) =>
              !EXCLUDED_CATEGORY_KEYWORDS.some((kw) =>
                opt.name.toLowerCase().includes(kw),
              ),
          );
          setVehicleTypeOptions(opts);
          const homeCategory =
            seedFilters?.category_ids?.[0] ??
            seedFilters?.tyreType?.predefined_id ??
            persistedState?.selectedVehicleType;

          if (
            homeCategory &&
            opts.some((o) => String(o.id) === String(homeCategory))
          ) {
            setSelectedVehicleType(String(homeCategory));
          } else {
            setSelectedVehicleType(opts[0]?.id ?? null);
          }
        }
      } catch (err) {
        console.error("Failed to fetch categories:", err);
      }
    };

    fetchCategories();
    return () => {
      cancelled = true;
    };
  }, [categoryParentID]);

  // ─────────────────────────────────────────────────────────────────
  // When productType + selectedVehicleType are known, load everything
  // that depends on category: BRAND, MAKE, TYRE-TYPE, TYRE-POSITION,
  // WIDTH, ASPECT, RIM. (Mirrors fetchSubPredefined in old code.)
  // ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!selectedVehicleType) return;

    const loadDependentPredefined = async () => {
      setBrandsLoading(true);
      dispatch(setLoading(true));

      const [
        brandRes,
        makeRes,
        tyreTypeRes,
        tyrePositionRes,
        widthRes,
        aspectRes,
        rimRes,
      ] = await Promise.all([
        predefinedFunc({
          entity_type: "BRAND",
          parent_id: productType?.predefined_id,
        }),
        predefinedFunc({
          entity_type: "MAKE",
          parent_id: selectedVehicleType,
        }),
        predefinedFunc({ entity_type: "TYRE-TYPE" }),
        predefinedFunc({ entity_type: "TYRE-POSITION" }),
        predefinedFunc({ entity_type: "WIDTH" }),
        predefinedFunc({ entity_type: "ASPECT" }),
        predefinedFunc({ entity_type: "RIM" }),
      ]);

      setBrandOptions(
        (brandRes?.data?.data ?? []).map(mapToOption).sort(sortOptions),
      );
      setMakes((makeRes?.data?.data ?? []).map(mapToOption).sort(sortOptions));
      setTyreTypeOptions(
        (tyreTypeRes?.data?.data ?? []).map(mapToOption).sort(sortOptions),
      );
      setTyrePositionOptions(
        (tyrePositionRes?.data?.data ?? []).map(mapToOption).sort(sortOptions),
      );
      setWidthOptions(
        (widthRes?.data?.data ?? []).map(mapToOption).sort(sortOptions),
      );
      setAspectOptions(
        (aspectRes?.data?.data ?? []).map(mapToOption).sort(sortOptions),
      );
      setRimOptions(
        (rimRes?.data?.data ?? []).map(mapToOption).sort(sortOptions),
      );

      setBrandsLoading(false);
      dispatch(setLoading(false));
    };

    loadDependentPredefined();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productType?.predefined_id, selectedVehicleType]);

  // ─────────────────────────────────────────────────────────────────
  // MODEL depends on Make, VARIANT depends on Model
  // ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!selectedMake) {
      setModels([]);
      return;
    }
    const fetchModels = async () => {
      const res = await predefinedFunc({
        entity_type: "MODEL",
        parent_id: selectedMake,
      });
      if (res.status === 200) {
        setModels((res.data?.data ?? []).map(mapToOption));
      }
    };
    fetchModels();
  }, [selectedMake]);

  useEffect(() => {
    if (!selectedModel) {
      setVariants([]);
      return;
    }
    const fetchVariants = async () => {
      const res = await predefinedFunc({
        entity_type: "VARIANT",
        parent_id: selectedModel,
      });
      if (res.status === 200) {
        setVariants((res.data?.data ?? []).map(mapToOption));
      }
    };
    fetchVariants();
  }, [selectedModel]);

  // ─────────────────────────────────────────────────────────────────
  // Debounce price slider
  // ─────────────────────────────────────────────────────────────────
  const handlePriceChange = (value: number) => {
    setPriceValue(value);
    clearTimeout(priceDebounceRef.current);
    priceDebounceRef.current = setTimeout(() => {
      setDebouncedPrice(value);
    }, 500);
  };

  // ─────────────────────────────────────────────────────────────────
  // Build the API payload from local filter state
  // ─────────────────────────────────────────────────────────────────
  // const buildPayload = () => {
  //   const authTokens = JSON.parse(localStorage.getItem("authTokens") || "{}");
  //   const userId = authTokens?.user?.id ?? null;

  //   return {
  //     page_number: pageNumber,
  //     page_size: rowsPerPageOption,
  //     user_id: userId,

  //     // category_ids: Array.isArray(initialFilters?.category_ids)
  //     //   ? initialFilters.category_ids
  //     //   : selectedVehicleType
  //     //     ? [selectedVehicleType]
  //     //     : [],
  //     category_ids: selectedVehicleType ? [selectedVehicleType] : [],
  //     // product_type_id: location.state?.product_type_id,
  //     product_type_id: productType?.predefined_id ?? null,
  const buildPayload = () => {
    const authTokens = JSON.parse(localStorage.getItem("authTokens") || "{}");
    const userId = authTokens?.user?.id ?? null;
    const companyId = authTokens?.user?.company_id ?? null;
    const departmentId = authTokens?.user?.department_id ?? null;
    // console.log("🔑 AUTH DEBUG:", {
    //   hasToken: !!authTokens?.access_token,
    //   userId,
    //   companyId: authTokens?.user?.company_id,
    //   departmentId: authTokens?.user?.department_id,
    //   roles: authTokens?.user?.roles,
    // });

    return {
      page_number: pageNumber,
      page_size: rowsPerPageOption,
      user_id: userId,
      company_id: companyId,
      department_id: departmentId,

      category_ids: selectedVehicleType ? [selectedVehicleType] : [],
      product_type_id: productType?.predefined_id ?? null,

      make_ids: selectedMake ? [selectedMake] : [],
      model_ids: selectedModel ? [selectedModel] : [],
      variant_ids: selectedVariant ? [selectedVariant] : [],

      width_ids: selectedWidth ? [selectedWidth] : [],
      aspect_ratio_ids: selectedAspect ? [selectedAspect] : [],
      rim_ids: selectedRim ? [selectedRim] : [],

      brand_ids: selectedBrandIds,

      price_ids: [],
      price_range_from: null,
      price_range_to: debouncedPrice === PRICE_MAX ? null : debouncedPrice,

      tyer_positions_ids: [],
      tyer_ps_ids: selectedTyrePositionIds,
      tyer_sizes_ids: [],
      tyer_type_ids: selectedTyreTypeIds,
      tyer_types_ids: [],
      sort_by: sortValue ? sortValue : null,
    };
  };
  const filtersKey = JSON.stringify({
    selectedVehicleType,
    selectedMake,
    selectedModel,
    selectedVariant,
    selectedWidth,
    selectedAspect,
    selectedRim,
    selectedBrandIds,
    selectedTyreTypeIds,
    selectedTyrePositionIds,
    debouncedPrice,
    productTypeId: productType?.predefined_id ?? null,
    sortValue,
  });

  const prevFiltersKeyRef = useRef(filtersKey);
  useEffect(() => {
    // IMPORTANT:
    // Do not call product API until required values are ready.
    if (!productType?.predefined_id) {
      return;
    }

    const categoryId = selectedVehicleType ?? vehicleTypeOptions[0]?.id;
    if (!categoryId) {
      return;
    }

    let isMounted = true;

    const filtersChanged = prevFiltersKeyRef.current !== filtersKey;

    prevFiltersKeyRef.current = filtersKey;

    const targetPage = filtersChanged ? 1 : pageNumber;

    if (filtersChanged) {
      setProducts([]);
      setPageNumber(1);
      setCurrentPage(1);
      loadedPages.current.clear();
    }

    if (loadedPages.current.has(targetPage)) {
      return;
    }

    const fetchProducts = async () => {
      const thisRequestId = ++requestIdRef.current;

      setLoadingMore(true);
      dispatch(setLoading(true));
      setError("");

      const payload = buildPayload();

      payload.page_number = targetPage;

      // console.log("PRODUCT API PAYLOAD:", JSON.stringify(payload, null, 2));
      // console.log(" PRODUCT REQUEST PAYLOAD:", payload);
      try {
        const [countResponse, response]: any = await Promise.all([
          getProductsListCount(payload),
          getProductList(payload),
        ]);

        if (!isMounted || thisRequestId !== requestIdRef.current) {
          return;
        }

        setTotalProducts(
          countResponse?.data?.count ?? countResponse?.count ?? 0,
        );

        const listResponse = response?.data ?? response?.result ?? [];

        const safeList = Array.isArray(listResponse) ? listResponse : [];

        const mappedProducts = safeList.map((item: any) => ({
          product_id: item.product_id,

          brand: {
            name: item.brand?.name ?? "",
            thumbnail: item.brand?.thumbnail ?? item.brand?.url ?? "",
          },

          name: item.product_name,
          sku: item.slug,

          product_offer_price: item.product_offer_price ?? 0,

          product_price: item.product_price ?? 0,

          cart_item_id: item.cart_item_id ?? null,

          rating: item.reviews_rating ?? 0,

          reviewCount: item.reviews_count ?? 0,

          compatible_vehicles: item.compatible_vehicles ?? "",

          document_as_product: item.document_as_product ?? [],

          image:
            item.document_as_product?.[0]?.document?.file_path ??
            "https://via.placeholder.com/300x200?text=No+Image",

          tags: item.product_feature?.map((f: any) => f.feature?.name) ?? [],
        }));

        setProducts((prev) => {
          const base = filtersChanged ? [] : prev;

          const existingIds = new Set(base.map((p: any) => p.product_id));

          const newProducts = mappedProducts.filter(
            (p: any) => !existingIds.has(p.product_id),
          );

          return [...base, ...newProducts];
        });

        loadedPages.current.add(targetPage);
      } catch (err: any) {
        console.error("Failed to fetch products:", err);

        if (isMounted && thisRequestId === requestIdRef.current) {
          setError(
            err?.message ?? "Unable to load products. Please try again.",
          );
        }
      } finally {
        if (isMounted && thisRequestId === requestIdRef.current) {
          dispatch(setLoading(false));
          setLoadingMore(false);
        }
      }
    };

    fetchProducts();

    return () => {
      isMounted = false;
    };
  }, [filtersKey, pageNumber, productType?.predefined_id, selectedVehicleType]);

  const lastProductRef = useCallback((node: HTMLDivElement | null) => {
    if (observer.current) {
      observer.current.disconnect();
      observer.current = null;
    }

    if (!node) return;

    observer.current = new IntersectionObserver(
      (entries) => {
        if (
          entries[0].isIntersecting &&
          !loadingMoreRef.current &&
          productsLengthRef.current < totalProductsRef.current
        ) {
          setLoadingMore(true);
          setPageNumber((prev) => prev + 1);
        }
      },
      { rootMargin: "200px" },
    );

    observer.current.observe(node);
  }, []);

  // ── FilterSidebar handlers ────────────────────────────────────────
  const onVehicleTypeChange = (id: string) => {
    setSelectedVehicleType(id);
    setSelectedMake(null);
    setSelectedModel(null);
    setSelectedVariant(null);
    // setSelectedBrandIds([]);
    setSelectedWidth(null);
    setSelectedAspect(null);
    setSelectedRim(null);
    // setSelectedTyreTypeIds([]);
    // setSelectedTyrePositionIds([]);
  };

  const onMakeChange = (id: string | null) => {
    setSelectedMake(id);
    setSelectedModel(null);
    setSelectedVariant(null);
  };

  const onModelChange = (id: string | null) => {
    setSelectedModel(id);
    setSelectedVariant(null);
  };

  const onVariantChange = (id: string | null) => {
    setSelectedVariant(id);
  };

  const onBrandToggle = (id: string) => {
    setSelectedBrandIds((prev) =>
      prev.includes(id) ? prev.filter((b) => b !== id) : [...prev, id],
    );
  };

  const onViewMoreBrands = () => {
    setVisibleBrandsCount((prev) => prev + INITIAL_VISIBLE_BRANDS);
  };

  const onTyreTypeToggle = (id: string) => {
    setSelectedTyreTypeIds((prev) =>
      prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id],
    );
  };

  const onTyrePositionToggle = (id: string) => {
    setSelectedTyrePositionIds((prev) =>
      prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id],
    );
  };

  const onResetAll = () => {
    setSelectedMake(null);
    setSelectedModel(null);
    setSelectedVariant(null);
    setSelectedBrandIds([]);
    setVisibleBrandsCount(INITIAL_VISIBLE_BRANDS);
    setSelectedWidth(null);
    setSelectedAspect(null);
    setSelectedRim(null);
    setSelectedTyreTypeIds([]);
    setSelectedTyrePositionIds([]);
    setPriceValue(PRICE_MAX);
    setDebouncedPrice(PRICE_MAX);
  };

  const onPageChange = (page: number) => {
    setCurrentPage(page);
    setPageNumber(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const handleSortChange = (value: string) => {
    setSortValue(value);

    // reset infinite scroll
    setProducts([]);
    setPageNumber(1);
    setCurrentPage(1);
    loadedPages.current.clear();
  };
  const breadcrumbs = [
    "Home",
    entityCode === "BATTERY" ? "Batteries" : "Tyres",
    // selectedVehicleName || "Passenger Car",
  ];
  const selectedVehicleName =
    vehicleTypeOptions.find(
      (item) => String(item.id) === String(selectedVehicleType),
    )?.name || "";

  const pageHeading =
    entityCode === "BATTERY"
      ? `${selectedVehicleName} Batteries`
      : `${selectedVehicleName} Tyres`;
  return (
    <div className={styles.page}>
      <BreadCrumbs />
      <div className={styles.layout}>
        {/* Sidebar */}
        <FilterSidebar
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          vehicleTypes={vehicleTypeOptions}
          selectedVehicleType={selectedVehicleType}
          onVehicleTypeChange={onVehicleTypeChange}
          makes={makes}
          models={models}
          variants={variants}
          selectedMake={selectedMake}
          selectedModel={selectedModel}
          selectedVariant={selectedVariant}
          onMakeChange={onMakeChange}
          onModelChange={onModelChange}
          onVariantChange={onVariantChange}
          showVariant={entityCode === "TYRE"}
          brands={brandOptions}
          selectedBrandIds={selectedBrandIds}
          onBrandToggle={onBrandToggle}
          visibleBrandsCount={visibleBrandsCount}
          onViewMoreBrands={onViewMoreBrands}
          brandsLoading={brandsLoading}
          showTyreSizes={entityCode === "TYRE"}
          widthOptions={widthOptions}
          aspectOptions={aspectOptions}
          rimOptions={rimOptions}
          selectedWidth={selectedWidth}
          selectedAspect={selectedAspect}
          selectedRim={selectedRim}
          onWidthChange={setSelectedWidth}
          onAspectChange={setSelectedAspect}
          onRimChange={setSelectedRim}
          priceMin={PRICE_MIN}
          priceMax={PRICE_MAX}
          priceValue={priceValue}
          onPriceChange={handlePriceChange}
          showTyreType={entityCode === "TYRE"}
          tyreTypeOptions={tyreTypeOptions}
          selectedTyreTypeIds={selectedTyreTypeIds}
          onTyreTypeToggle={onTyreTypeToggle}
          showTyrePosition={entityCode === "TYRE"}
          tyrePositionOptions={tyrePositionOptions}
          selectedTyrePositionIds={selectedTyrePositionIds}
          onTyrePositionToggle={onTyrePositionToggle}
          onResetAll={onResetAll}
        />

        {/* Main */}
        <main className={styles.main}>
          {/* <h1 className={styles.pageTitle}>Passenger Car Tyres</h1> */}
          <h1 className={styles.pageTitle}>{pageHeading}</h1>
          <SortBar
            totalItems={totalProducts}
            currentPage={pageNumber}
            itemsPerPage={rowsPerPageOption}
            sortValue={sortValue}
            onSortChange={handleSortChange}
            onFilterToggle={() => setSidebarOpen(true)}
          />
          <ProductGrid
            products={products}
            code={entityCode}
            loadingMore={loadingMore}
          />
          {loadingMore && (
            <AppSkeleton
              count={8}
              width="300px"
              height="300px"
              borderRadius="12px"
              containerClassName="popular-brands-skeleton-grid"
              className="popular-brands-skeleton-item"
            />
          )}
          <div ref={lastProductRef} style={{ height: "1px" }} />
        </main>
      </div>
    </div>
  );
};

export default PassengerCarTyres;
